import { fetch } from '../../utils';
import Backend from '../../backend';

/**
 * The adviser's own notes on a client, stored on the factsheet
 * (docs/specs/2026-09-24-demo-localstorage-to-db-design.md §4.1, §17).
 * One endpoint writes them all; the server whitelists the field names.
 */

export type RecruitAxis = 'motive' | 'network' | 'comms' | 'discipline' | 'family';

export type RecruitmentNotes = {
  stage?: string;
  stageUpdatedAt?: string;
  scores?: Partial<Record<RecruitAxis, number>>;
  path?: string;
  who?: { yearsOfExp?: number; network?: string; targetIncome?: number };
  painPoints?: string[];
  concerns?: string[];
  strengths?: string[];
  updatedAt?: string;
};

/** 你希望的服務方式 imported from 保障小測驗: option keys of S1, S4 and S5 (views/CoverQuiz/coverServicePrefs.ts). */
export type ServicePrefs = { contact?: string | null; info?: string | null; priority?: string | null };

export type SmartIdeaNotes = { objectionMarks?: string[]; servicePrefs?: ServicePrefs | null };

export type QuizCompletedAt = { cover?: string; career?: string };

/** Until when an unanswered part-1 link of each quiz is live (server factsheet.quizPendingUntil). */
export type QuizPendingUntil = { cover?: string; career?: string };

/** `null` clears a field (or the whole `scores` / `who`). */
export type AdviserNotesPatch = {
  recruitment?: {
    stage?: string | null;
    path?: string | null;
    scores?: Partial<Record<RecruitAxis, number | null>> | null;
    who?: { yearsOfExp?: number | null; network?: string | null; targetIncome?: number | null } | null;
    painPoints?: string[] | null;
    concerns?: string[] | null;
    strengths?: string[] | null;
  };
  smartIdea?: { objectionMarks?: string[] | null; servicePrefs?: ServicePrefs | null };
};

export type AdviserNotesSaved = { recruitment: RecruitmentNotes | null; smartIdea: SmartIdeaNotes | null };

/** Resolves with the server's copy of both subdocuments; throws on any failure. */
export const patchAdviserNotes = async (factsheetId: string, body: AdviserNotesPatch): Promise<AdviserNotesSaved> => {
  const response = await fetch(Backend.methodUrl(`factsheets/${encodeURIComponent(factsheetId)}/adviser-notes`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const payload = await response.json();
  if (!payload || payload.success !== true) {
    throw Object.assign(new Error('adviser-notes failed'), { payload });
  }
  return { recruitment: payload.recruitment ?? null, smartIdea: payload.smartIdea ?? null };
};
